<template>
  <div class="d-flex flex-column h-100 p-0">
    <div v-if="!selectedDialog" class="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
      Выберите/создайте диалог для начала общения
    </div>
    <div v-else class="d-flex flex-column flex-grow-1 overflow-hidden">
      <div class="p-3 border-bottom">
        <strong>{{ selectedDialog.client_name }}</strong>
      </div>
      <div class="message-block flex-grow-1 overflow-auto p-3" @scroll.passive="handleScroll" ref="chatContainer">
        <div v-for="(group, index) in groupedMessages" :key="index" class="mb-3">

          <div v-if="group.type === 'date'" class="date-separator text-center text-muted my-2">
            <span>{{ group.date }}</span>
          </div>

          <div v-else class="d-flex" :class="group.isMine ? 'justify-content-end' : 'justify-content-start'">
            <div :class="group.isMine ? 'bg-light-2' : 'bg-light'" class="message rounded p-2" style="max-width: 75%;">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <small class="text-muted" style="font-size: 0.75em;">
                  {{ formatDate(group.messages[0].created_at) }}
                </small>
              </div>

              <div v-for="message in group.messages" :key="message.id" :data-message-id="message.id" class="mb-2">
                <div v-if="shouldShowContent(message) && isHtmlAllowed()" v-html="message.content" class="mb-1"></div>
                <div v-else-if="shouldShowContent(message)" class="mb-1">{{ message.content }}</div>

                <div v-if="fileUrl(message)" class="attachments mt-1">
                  <!-- existing attachments logic unchanged -->
                  <template v-if="isImageUrl(fileUrl(message))">
                    <el-image
                      :src="fileUrl(message)"
                      :preview-src-list="[fileUrl(message)]"
                      fit="cover"
                      style="max-width: 240px; border-radius: 4px"
                    />
                    <div class="attach-actions mt-1">
                      <a
                        :href="fileUrl(message)"
                        :download="attachmentName(message)"
                        class="small"
                      >
                        ⬇️ Скачать
                      </a>
                    </div>
                  </template>
                  <template v-else-if="isVideoUrl(fileUrl(message))">
                    <video
                      :src="fileUrl(message)"
                      controls
                      style="max-width: 240px; border-radius: 4px"
                    />
                    <div class="attach-actions mt-1">
                      <a
                        :href="fileUrl(message)"
                        :download="attachmentName(message)"
                        class="small"
                      >
                        ⬇️ Скачать
                      </a>
                    </div>
                  </template>
                  <template v-else-if="isAudioUrl(fileUrl(message))">
                    <audio
                      :src="fileUrl(message)"
                      controls
                      style="width: 240px;"
                    />
                    <div class="attach-actions mt-1">
                      <a
                        :href="fileUrl(message)"
                        :download="attachmentName(message)"
                        class="small"
                      >
                        ⬇️ Скачать
                      </a>
                    </div>
                  </template>
                  <template v-else>
                    <a
                      :href="fileUrl(message)"
                      :download="attachmentName(message)"
                      class="d-inline-block mt-1"
                    >
                      📎 {{ attachmentName(message) }}
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
    <div class="border-top p-2 d-flex" v-if="selectedDialog">
      <div class="textarea-block">
        <div class="file-upload-block w-100 d-flex align-items-start mb-2 gap-2">
          <el-upload
            ref="uploadRef"
            class="w-100 d-none"
            :auto-upload="false"
            :action="uploadUrl"
            :data="{ client_id: selectedDialog?.client_id }"
            :show-file-list="false"
            :on-remove="handleUploadRemove"
            :before-upload="beforeUpload"
            :on-change="handleFileChange"
            :headers="{ Authorization: 'Bearer ' + getToken() }"
            v-model:file-list="uploadFiles">
            <font-awesome-icon :icon="['fas', 'paperclip']" size="xl" />
          </el-upload>
          <div v-if="uploadFiles.length" class="selected-files w-100 d-flex flex-column gap-2 mt-1">
            <div v-for="file in uploadFiles" :key="file.uid" class="file-item d-flex align-items-center gap-2">
              <font-awesome-icon :icon="['fas', 'paperclip']" />
              <span class="file-name text-truncate">{{ file.name }}</span>
              <el-progress
                v-if="typeof file.percentage === 'number'"
                :percentage="Math.round(file.percentage)"
                :stroke-width="6"
                class="flex-grow-1"
                :status="file.status === 'success' ? 'success' : (file.status === 'fail' ? 'exception' : undefined)"
              />
              <el-tag v-else type="info" size="small">Готов к отправке</el-tag>
              <el-button size="small" circle @click="removeFile(file)" :aria-label="`Удалить ${file.name}`">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </el-button>
            </div>
          </div>
        </div>
        <div class="input-block w-100 d-flex flex-column">
          <div class="editor-toolbar d-flex align-items-center mb-1">
            <div class="d-flex align-items-center gap-1"></div>
            <div class="d-flex align-items-center gap-1">
              <el-button size="small" plain @click="triggerFileSelect">
                <font-awesome-icon :icon="['fas', 'paperclip']" />
              </el-button>
            </div>
          </div>
          <QuillEditor
            ref="editor"
            v-model:content="messageHtml"
            contentType="html"
            theme="snow"
            :toolbar="[['bold','italic','strike','link','blockquote','code-block']]"
            class="w-100"
            style="min-height:64px;border-radius:4px;"
            @keydown.enter.exact.prevent="sendMessage"
          />
          <el-button
            class="btn btn-success mt-2"
            @click="sendMessage"
            :disabled="isSending || (!messageHtml.trim() && pendingFiles.length === 0)"
          >
            Отправить
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { mapState } from 'vuex'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import axios from 'axios'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'ChatPanel',
  components: { QuillEditor },
  props: {
    selectedDialog: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingOlder: false,
      messageHtml: '',
      page: 1,
      pageSize: 25,
      seenFlushTimeout: null,
      observerInitialized: false,
      uploadUrl: this.$store.getters['messengers/getUploadUrl'],
      uploadFiles: [],
      pendingFiles: [],
      isSending: false,
      sendFileUrl: 'https://lk.lineon.me/api/messages.sendFile',
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('messengers', ['messages', 'messengers']),
    ...mapState('socket', ['waAuthCheckInProgress', 'whatsappReadyStatuses']),
    sortedMessages() {
      const dialogId = this.selectedDialog?.id
      if (!dialogId) return []
      return this.messages
        .filter(m => String(m.dialog_id) === String(dialogId))
        .map(m => ({
          ...m,
          id: Number(m.id),
          dialog_id: Number(m.dialog_id),
          sender_user_id: m.sender_user_id ? Number(m.sender_user_id) : null,
          created_at: new Date(Number(m.created_at) * 1000),
          updated_at: m.updated_at ? new Date(Number(m.updated_at) * 1000) : null,
          attachments: m.attachments ? (typeof m.attachments === 'string' ? (() => {
            try {
              return JSON.parse(m.attachments)
            } catch {
              return null
            }
          })() : m.attachments) : null,
          sender_name: m.sender_name || ''
        }))
        .sort((a, b) => a.created_at - b.created_at)
    },
    groupedMessages() {
      const groups = []
      let currentGroup = null
      let lastDate = null

      for (const msg of this.sortedMessages) {
        const msgDate = dayjs(msg.created_at).format('DD.MM.YYYY')

        if (msgDate !== lastDate) {
          groups.push({
            type: 'date',
            date: msgDate
          })
          lastDate = msgDate
        }

        const timestamp = msg.created_at.getTime()

        if (
          currentGroup &&
          currentGroup.type === 'messages' &&
          currentGroup.sender_type === msg.sender_type &&
          currentGroup.sender_user_id === msg.sender_user_id &&
          Math.abs(timestamp - currentGroup.lastTimestamp) <= 60000
        ) {
          currentGroup.messages.push(msg)
          currentGroup.lastTimestamp = timestamp
        } else {
          if (currentGroup) groups.push(currentGroup)
          currentGroup = {
            type: 'messages',
            sender_type: msg.sender_type,
            sender_user_id: msg.sender_user_id,
            isMine: msg.sender_type === 'manager',
            messages: [msg],
            lastTimestamp: timestamp
          }
        }
      }

      if (currentGroup) groups.push(currentGroup)
      return groups
    }
  },
  watch: {
    selectedDialog: {
      immediate: true,
      handler(newVal, oldVal) {
        try {
          const token = this.$cookies.get('token') || (this.$store.state && this.$store.state.cookies && this.$store.state.cookies.get && this.$store.state.cookies.get('token'))
          if (token) {
            const decodeJwt = (typeof window !== 'undefined' && window.decodeJwt) ? window.decodeJwt : (require && require('jwt-decode'))
            const decoded = typeof decodeJwt === 'function' ? decodeJwt(token) : decodeJwt.default(token)
            const accountId = decoded.account_id
            if (oldVal && oldVal.id) {
              const oldChannel = `new-message_${accountId}_${oldVal.id}`
              if (this.$store.state.socket && this.$store.state.socket.socket && this.$store.state.socket.socket.off) {
                this.$store.state.socket.socket.off(oldChannel)
              }
            }
          }
        } catch (e) {
          // ignore decode errors
        }

        if (!newVal) {
          this.pendingFiles = []
        }

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    messages() {
      this.scrollToBottom()
    },
    sortedMessages(newVal) {
      if (newVal.length && !this.observerInitialized) {
        this.observerInitialized = true
        this.$nextTick(() => {
          const container = this.$refs.chatContainer
          if (!container) return
          const allEls = container.querySelectorAll('[data-message-id]')
          allEls.forEach(el => {
            this.observer.observe(el)
          })
        })
      }
    }
  },
  methods: {
    triggerFileSelect() {
      const upload = this.$refs.uploadRef
      if (upload && upload.$el) {
        const input = upload.$el.querySelector('input[type=file]')
        if (input) input.click()
      }
    },
    getToken() {
      return document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] || ''
    },
    handleUploadProgress(event, file, fileList) {
      this.uploadFiles = fileList
    },
    handleUploadRemove(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(f => f)
    },
    removeFile(file) {
      this.uploadFiles = this.uploadFiles.filter(f => f.uid !== file.uid)
      this.pendingFiles = this.pendingFiles.filter(f => f.uid !== file.uid)
    },
    isMine(message) {
      return message.sender_type === 'manager'
    },
    formatDate(date) {
      if (!date) return ''

      const accountTz = this.user.timezone || 'Europe/Moscow'

      const local = dayjs(date).tz(accountTz)
      const now = dayjs().tz(accountTz)
      const yesterday = now.subtract(1, 'day')

      if (local.isSame(now, 'day')) {
        return `Сегодня ${local.format('HH:mm')}`
      } else if (local.isSame(yesterday, 'day')) {
        return `Вчера ${local.format('HH:mm')}`
      } else {
        return local.isValid() ? local.format('DD.MM.YYYY HH:mm') : ''
      }
    },
    async handleScroll() {
      const container = this.$refs.chatContainer
      if (container.scrollTop === 0 && !this.loadingOlder) {

        const prevHeight = container.scrollHeight

        this.loadingOlder = true
        this.page += 1

        await this.$store.dispatch('messengers/fetchMessages', {
          dialog_id: this.selectedDialog.id,
          page: this.page,
          pageSize: this.pageSize
        })

        this.loadingOlder = false

        this.$nextTick(() => {
          const newHeight = container.scrollHeight
          container.scrollTop = newHeight - prevHeight
        })
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
      })
    },
    isHtmlAllowed() {
      const messenger = this.messengers.find(m => m.id === this.selectedDialog?.messenger_id)
      // Разрешаем HTML для email и telegram
      return ['email', 'telegram', 'whatsapp'].includes(messenger?.type)
    },
    htmlToWhatsapp(html) {
      if (!html) return ''
      const BT = '\u0060' // ASCII backtick to avoid locale issues
      const stripHtml = (h) => (h || '').replace(/<[^>]+>/g, '').replace(/\u00a0/g, ' ')

      let s = html
        // переносы строк
        .replace(/<br\s*\/?>(?!\n)/gi, '\n')
        .replace(/<\/p\s*>/gi, '\n')
        .replace(/<\/div\s*>/gi, '\n')

      // code block: <pre><code>...</code></pre> или просто <pre>...</pre>
      s = s
        .replace(/<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi, (m, inner) => {
          const body = stripHtml(inner)
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .split('\n')
            .map(line => (line.length ? `${BT}${line}${BT}` : ''))
            .join('\n')
          return body
        })
        .replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, (m, inner) => {
          const body = stripHtml(inner)
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .split('\n')
            .map(line => (line.length ? `${BT}${line}${BT}` : ''))
            .join('\n')
          return body
        })

      // inline code
      s = s.replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, (m, inner) => `${BT}${stripHtml(inner)}${BT}`)

      // цитаты: blockquote -> >
      s = s.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (m, inner) =>
        stripHtml(inner)
          .split(/\r?\n/)
          .map(line => (line.trim() ? '> ' + line : ''))
          .join('\n')
      )

      // жирный/курсив/зачеркнутый (сохраняем пробелы снаружи, тримим внутри)
      const wrapTrim = (txt, l, r = l) => {
        const t = stripHtml(txt).replace(/\s+/g, ' ').trim()
        return t ? `${l}${t}${r}` : ''
      }

      s = s
        .replace(/(\s*)<(?:b|strong)[^>]*>([\s\S]*?)<\/\s*(?:b|strong)\s*>(\s*)/gi, (m, lead, inner, trail) => `${lead}${wrapTrim(inner, '*')}${trail}`)
        .replace(/(\s*)<(?:i|em)[^>]*>([\s\S]*?)<\/\s*(?:i|em)\s*>(\s*)/gi, (m, lead, inner, trail) => `${lead}${wrapTrim(inner, '_')}${trail}`)
        .replace(/(\s*)<(?:s|strike|del)[^>]*>([\s\S]*?)<\/\s*(?:s|strike|del)\s*>(\s*)/gi, (m, lead, inner, trail) => `${lead}${wrapTrim(inner, '~')}${trail}`)

      // подстрахуемся: если два маркера идут подряд без пробела — добавим пробел
      s = s.replace(/([*~_])(\s*)([*~_])/g, (m, a, sp, b) => `${a}${sp || ' '}${b}`)

      // ссылки: <a href="...">текст</a> -> "текст (url)" или просто url
      s = s.replace(/<a [^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi, (m, href, text) => {
        const t = stripHtml(text).trim()
        const cleanHref = href.trim()
        if (!t || t === cleanHref) return cleanHref
        return `${t} (${cleanHref})`
      })

      // списки: превращаем в строки с дефисом
      s = s
        .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (m, inner) => `- ${stripHtml(inner).trim()}\n`)
        .replace(/<\/?ul[^>]*>/gi, '')
        .replace(/<\/?ol[^>]*>/gi, '')

      // удаляем остатки тегов
      s = s.replace(/<[^>]+>/g, '')

      // чистим пробелы/пустые строки
      s = s.replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n')
      s = s.replace(/\n{3,}/g, '\n\n').trim()

      return s
    },
    async sendMessage() {
      const html = (this.messageHtml || '').trim()
      if (!html && this.pendingFiles.length === 0) return
      const messenger = this.messengers.find(m => m.id === this.selectedDialog.messenger_id)
      if (!messenger) {
        return
      }
      if (messenger.type === 'whatsapp') {
        this.isSending = true
        try {
          if (this.pendingFiles.length > 0) {
            const rawHtml = html
            for (let i = 0; i < this.pendingFiles.length; i++) {
              const file = this.pendingFiles[i]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('caption', i === 0 ? rawHtml : '')
              formData.append('content', rawHtml)
              formData.append('content_raw', rawHtml)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_phone))
              // backward-compat for PHP fallback resolution
              formData.append('client_phone', this.selectedDialog.client_phone)
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: (e) => {
                    if (e.total) file.percentage = Math.round((e.loaded / e.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (err) {
                console.error('sendFile error', err)
                file.status = 'fail'
              }
              await new Promise(r => setTimeout(r, 300))
            }

            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-whatsapp-message',
              data: {
                messenger_id: messenger.id,
                client_id: Number(this.selectedDialog.client_phone),
                dialog_id: Number(this.selectedDialog.id),
                content: html
              }
            })
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'email') {
        this.isSending = true
        try {
          const formData = new FormData()
          formData.append('dialog_id', Number(this.selectedDialog.id))
          formData.append('text', this.messageHtml.trim())

          for (const file of this.pendingFiles) {
            formData.append('files[]', file.raw)
          }

          await axios.post('https://lk.lineon.me/api/email.send', formData, {
            headers: { Authorization: 'Bearer ' + this.getToken() }
          });

          this.pendingFiles = []
          this.uploadFiles = []
          this.messageHtml = ''
          if (this.$refs.editor && this.$refs.editor.setHTML) {
            this.$refs.editor.setHTML('')
          }
          this.scrollToBottom()
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'telegram') {
        this.isSending = true
        try {
          const text = (this.messageHtml || '').trim()
          if (!text && this.pendingFiles.length === 0) return

          if (this.pendingFiles.length > 0) {
            for (let i = 0; i < this.pendingFiles.length; i++) {
              const file = this.pendingFiles[i]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_id))
              formData.append('caption', i === 0 ? text : '')
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: (e) => {
                    if (e.total) file.percentage = Math.round((e.loaded / e.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (err) {
                console.error('sendFile error', err)
                file.status = 'fail'
              }
              await new Promise(r => setTimeout(r, 300))
            }
            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            // Отправляем через сокет
            this.$store.dispatch('socket/send', {
              event: 'send-telegram-message',
              data: {
                messenger_id: messenger.id,
                dialog_id: Number(this.selectedDialog.id),
                client_id: Number(this.selectedDialog.client_id),
                content: text
              }
            })
            // Очистка после отправки
            this.messageHtml = ''
            this.uploadFiles = []
            this.pendingFiles = []
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      }
    },
    beforeUpload() {
      if (!this.selectedDialog || !this.selectedDialog.client_id) {
        this.$message.warning('Выберите диалог перед загрузкой файла')
        return false
      }
      // Validation only, do not mutate pendingFiles here
      return false // предотвращает автоматическую отправку
    },
    handleFileChange(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(f => f)
    },
    fileUrl(msg) {
      if (msg && typeof msg.file_url === 'string' && msg.file_url) return msg.file_url
      const att = msg && msg.attachments
      if (!att) return ''
      if (typeof att === 'string') return att
      if (Array.isArray(att) && att.length) {
        const first = att[0]
        if (typeof first === 'string') return first
        if (first && typeof first === 'object' && first.url) return first.url
      }
      if (typeof att === 'object' && att.url) return att.url
      return ''
    },
    isImageUrl(url) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url || '')
    },
    isVideoUrl(url, msg) {
      const mime = (msg?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('video/')
      const ext = (url || '').toLowerCase()
      return ext.endsWith('.mp4') || ext.endsWith('.webm')
    },
    isAudioUrl(url, msg) {
      const mime = (msg?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('audio/')
      const ext = (url || '').toLowerCase()
      return /\.(mp3|m4a|aac|ogg|oga|wav|opus)$/i.test(ext)
    },
    attachmentName(msg) {
      const url = this.fileUrl(msg) || ''
      try {
        const last = url.split('/').pop() || ''
        const decoded = decodeURIComponent(last)
        return decoded || 'файл'
      } catch {
        return 'файл'
      }
    },
    shouldShowContent(msg) {
      const text = (msg && msg.content ? String(msg.content) : '').trim()
      if (!text) return false
      const url = this.fileUrl(msg)
      if (!url) return true
      // If text equals the attachment's filename, don't duplicate it as a separate message line
      try {
        const name = this.attachmentName(msg).trim()
        return text !== name
      } catch (_) {
        return true
      }
    }
  },
  mounted() {
    this.seenMessages = new Set()
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const msgId = entry.target.getAttribute('data-message-id')
          const msg = this.sortedMessages.find(m => String(m.id) === String(msgId))
          if (msg && !msg.read_at && msg.sender_type !== 'manager') {
            this.seenMessages.add(msg.id)
            if (this.seenFlushTimeout) {
              clearTimeout(this.seenFlushTimeout)
            }
            this.seenFlushTimeout = setTimeout(() => {
              const ids = Array.from(this.seenMessages)
              if (ids.length) {
                this.seenMessages.clear()
                this.$store.dispatch('messengers/markAsReadBatch', {
                  message_ids: ids,
                  dialog_id: Number(this.selectedDialog.id),
                  messenger_type: this.selectedDialog.messenger_type
                })
              }
              this.seenFlushTimeout = null
            }, 500)
          }
        }
      })
    }, {
      threshold: 0.5
    })

    // Инициализация scrollToBottom и наблюдателя только после полной отрисовки сообщений
    this.$nextTick(() => {
      setTimeout(() => {
        const container = this.$refs.chatContainer
        if (!container) return
        const allEls = container.querySelectorAll('[data-message-id]')
        allEls.forEach(el => {
          this.observer.observe(el)
        })
        this.scrollToBottom()
      }, 50)
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>
<style scoped>
.textarea-block {
  box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;
  border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
  width: 100%;
  text-align: start;
  padding: 0 10px 15px;;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 0;
}

.message {
  text-align: start;
  min-width: 220px;
}

.bg-light-2 {
  --bs-bg-opacity: 1;
  background-color: rgb(195 230 250) !important;
}

.message-block {
  height: calc(100% - 125px);
}

.attachments a {
  color: #0d6efd;
  text-decoration: underline;
}

.input-block {
  flex-grow: 1;
}

.editor-toolbar {
  padding: 0 4px;
}

.file-item .file-name {
  max-width: 50%;
}

@media (max-width: 992px) {
  .file-item .file-name {
    max-width: 40%;
  }
}

.selected-files .el-progress {
  min-width: 120px;
}
</style>

.date-separator {
  font-size: 13px;
  font-weight: 500;
  opacity: 0.7;
  position: relative;
}

.date-separator span {
  background: #fff;
  padding: 0 10px;
  position: relative;
  z-index: 2;
}

.date-separator::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
  background: #ccc;
  z-index: 1;
}
